import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Modal,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDistributionLists } from "../hooks";
import AccessibleIconButton from "components/base/AccessibleIconButton";

export default function CreateDistributionListPopup({
  open,
  onClose,
  onCreated,
}) {
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const { createDistributionList, loading, getDistributionLists } =
    useDistributionLists();

  const validateName = (value) => {
    if (!value.trim()) {
      return "Name is required";
    }
    if (!/^[a-zA-Z0-9\s_-]+$/.test(value)) {
      return "Name can only contain letters, numbers, spaces, underscores, and hyphens";
    }
    if (value.length > 50) {
      return "Name must be 50 characters or less";
    }
    return "";
  };

  const handleSubmit = async () => {
    const validationError = validateName(name);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      const newList = await createDistributionList(name.trim());
      setName("");
      setError("");
      if (onCreated) {
        onCreated(newList);
      }
      onClose();
    } catch (error) {
      if (
        error.response?.data?.errors?.some((err) =>
          err.message?.includes("unique")
        )
      ) {
        setError("A distribution list with this name already exists");
      } else {
        setError("Failed to create distribution list");
      }
    }
  };

  const handleClose = () => {
    setName("");
    setError("");
    onClose();
  };

  const handleNameChange = (event) => {
    const value = event.target.value;
    setName(value);
    if (error) {
      setError(validateName(value));
    }
  };

  useEffect(() => {
    getDistributionLists();
  }, []);

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "100vw", sm: "50vw", md: "34vw" },
    bgcolor: "background.paper",
    boxShadow: 0,
    borderRadius: 2,
  };

  return (
    <Modal open={open} onClose={loading ? undefined : handleClose}>
      <Box sx={modalStyle}>
        <Box sx={{ px: 5, py: 4 }}>
          <Typography
            variant="h4"
            component="div"
            color="#406da4"
            fontWeight="600"
          >
            Add Distribution List
          </Typography>
          <AccessibleIconButton
            title="Close"
            onClick={handleClose}
            disabled={loading}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </AccessibleIconButton>
          <TextField
            fullWidth
            sx={{ mt: 2 }}
            label="List Name"
            value={name}
            onChange={handleNameChange}
            error={!!error}
            helperText={
              error ||
              "Names can only contain Letters, Numbers, Underscores and Hyphens."
            }
            disabled={loading}
          />
        </Box>
        <Box
          sx={{
            backgroundColor: "secondary.main",
            display: "flex",
            justifyContent: "space-between",
            px: 5,
            py: 3,
            borderRadius: "0 0 20px 20px",
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={loading}
            sx={{ width: "45%" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !name.trim()}
            startIcon={loading && <CircularProgress size={16} />}
            sx={{ width: "45%" }}
          >
            Add
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
